import { useState, useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { X, Check, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChatPreview } from "./ChatPreview";

// Survey option types
interface SurveyOption {
  id: string;
  label: string;
}

// Survey data structure
export interface OnboardingSurveyData {
  howDidYouHear?: string;
  lookingToDo?: string;
  mobileNumber?: string;
}

interface OnboardingSurveyPopupProps {
  open: boolean;
  onClose: () => void;
  onStepComplete: (data: Partial<OnboardingSurveyData>) => Promise<void>;
  initialData?: OnboardingSurveyData;
}

// Step 1 options - How did you hear about us?
const HOW_DID_YOU_HEAR_OPTIONS: SurveyOption[] = [
  { id: "facebook_instagram", label: "Facebook or Instagram" },
  { id: "google", label: "Google" },
  { id: "reddit", label: "Reddit" },
  { id: "clover_app_market", label: "Clover App Market" },
  { id: "friend", label: "A friend told me" },
  { id: "other", label: "Other" }
];

// Step 2 options - What are you looking to do?
const LOOKING_TO_DO_OPTIONS: SurveyOption[] = [
  {
    id: "bring_customers",
    label: "Bring in more customers or increase repeat visits"
  },
  { id: "send_sms", label: "Send SMS to my customers" },
  { id: "understand_customers", label: "Understand my customers better" },
  { id: "product_performance", label: "See which products are doing well" },
  { id: "manage_customers", label: "Manage customer details" },
  { id: "other", label: "Other" }
];

// Country codes for phone input
const COUNTRY_CODES = [
  { id: "US", code: "+1", label: "US", flag: "🇺🇸" },
  { id: "CA", code: "+1", label: "CA", flag: "🇨🇦" },
  { id: "UK", code: "+44", label: "UK", flag: "🇬🇧" },
  { id: "IN", code: "+91", label: "IN", flag: "🇮🇳" },
  { id: "AU", code: "+61", label: "AU", flag: "🇦🇺" }
];

// Reusable Option Card Component
interface OptionCardProps {
  selected: boolean;
  onClick: () => void;
  label: string;
  multiSelect?: boolean;
}

const OptionCard = ({
  selected,
  onClick,
  label,
  multiSelect = false
}: OptionCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex items-center gap-3 w-full p-4 rounded-lg border-2 transition-all duration-200 text-left",
      "hover:border-[var(--primary)] hover:bg-[var(--primary)]/5",
      selected
        ? "border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)]"
        : "border-gray-200 bg-white text-gray-700"
    )}
  >
    {multiSelect && (
      <div
        className={cn(
          "flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
          selected
            ? "border-[var(--primary)] bg-[var(--primary)]"
            : "border-gray-300"
        )}
      >
        {selected && <Check className="w-3 h-3 text-white" />}
      </div>
    )}
    <span className="text-sm font-medium">{label}</span>
  </button>
);

// Progress Bar Component
interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
}

const ProgressBar = ({ currentStep, totalSteps }: ProgressBarProps) => (
  <div className="flex gap-2">
    {Array.from({ length: totalSteps }).map((_, index) => (
      <div
        key={index}
        className={cn(
          "h-1 flex-1 rounded-full transition-colors duration-300",
          index < currentStep ? "bg-[var(--primary)]" : "bg-gray-200"
        )}
      />
    ))}
  </div>
);

export const OnboardingSurveyPopup = ({
  open,
  onClose,
  onStepComplete,
  initialData = {}
}: OnboardingSurveyPopupProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [howDidYouHear, setHowDidYouHear] = useState<string>(
    initialData.howDidYouHear || ""
  );
  const [lookingToDo, setLookingToDo] = useState<Set<string>>(
    new Set(initialData.lookingToDo?.split(",").filter(Boolean) || [])
  );
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const submitLockRef = useRef(false);

  // "Other" text inputs for step 1 and 2
  const [otherHowDidYouHear, setOtherHowDidYouHear] = useState("");
  const [otherLookingToDo, setOtherLookingToDo] = useState("");

  const totalSteps = 3;

  useEffect(() => {
    if (currentStep !== 3) setPhoneError("");
  }, [currentStep]);

  const formatPhoneDisplay = useCallback((value: string) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }, []);

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, "").slice(0, 10);
      setPhoneNumber(value);
      if (phoneError) setPhoneError("");
    },
    [phoneError]
  );

  const toggleLookingToDoOption = useCallback((optionId: string) => {
    setLookingToDo((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(optionId)) newSet.delete(optionId);
      else newSet.add(optionId);
      return newSet;
    });
  }, []);

  const saveCurrentStepData = useCallback(async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      const data: Partial<OnboardingSurveyData> = {};

      if (currentStep >= 1 && howDidYouHear) {
        data.howDidYouHear =
          howDidYouHear === "other" && otherHowDidYouHear.trim()
            ? `other:${otherHowDidYouHear.trim()}`
            : howDidYouHear;
      }

      if (currentStep >= 2 && lookingToDo.size > 0) {
        const selections = Array.from(lookingToDo).map((id) => {
          if (id === "other" && otherLookingToDo.trim())
            return `other:${otherLookingToDo.trim()}`;
          return id;
        });
        data.lookingToDo = selections.join(",");
      }

      if (currentStep >= 3 && phoneNumber) {
        const country = COUNTRY_CODES.find((c) => c.id === selectedCountry);
        data.mobileNumber = `${country?.code || "+1"}${phoneNumber}`;
      }

      await onStepComplete(data);
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  }, [
    currentStep,
    howDidYouHear,
    otherHowDidYouHear,
    lookingToDo,
    otherLookingToDo,
    selectedCountry,
    phoneNumber,
    onStepComplete
  ]);

  const handleNext = useCallback(async () => {
    if (currentStep === totalSteps) {
      if (!phoneNumber || phoneNumber.length < 10) {
        setPhoneError("Phone number is required to send your 2025 Wrapped");
        return;
      }
    }

    await saveCurrentStepData();
    if (currentStep < totalSteps) setCurrentStep((prev) => prev + 1);
    else onClose();
  }, [currentStep, totalSteps, phoneNumber, saveCurrentStepData, onClose]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  }, [currentStep]);

  const handleSkip = useCallback(async () => {
    await saveCurrentStepData();
    if (currentStep < totalSteps) setCurrentStep((prev) => prev + 1);
    else onClose();
  }, [currentStep, totalSteps, saveCurrentStepData, onClose]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                How did you hear about us?
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Many of our users find us on Facebook or Instagram — how did you
                hear about us?
              </DialogDescription>
            </div>

            {/* ✅ mobile: 1 col, >=sm: 2 col */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
              {HOW_DID_YOU_HEAR_OPTIONS.map((option) => (
                <OptionCard
                  key={option.id}
                  selected={howDidYouHear === option.id}
                  onClick={() => setHowDidYouHear(option.id)}
                  label={option.label}
                />
              ))}
            </div>

            {howDidYouHear === "other" && (
              <Input
                type="text"
                placeholder="Type what you need..."
                value={otherHowDidYouHear}
                onChange={(e) => setOtherHowDidYouHear(e.target.value)}
                className="mt-4 focus-visible:ring-[var(--primary)] focus-visible:border-[var(--primary)]"
                autoFocus
              />
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-2">
            <div className="space-y-2">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                What are you looking to do?
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Select all that apply — we'll personalize your experience.
              </DialogDescription>
            </div>

            {/* ✅ mobile: 1 col, >=sm: 2 col */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
              {LOOKING_TO_DO_OPTIONS.map((option) => (
                <OptionCard
                  key={option.id}
                  selected={lookingToDo.has(option.id)}
                  onClick={() => toggleLookingToDoOption(option.id)}
                  label={option.label}
                  multiSelect
                />
              ))}
            </div>

            {lookingToDo.has("other") && (
              <Input
                type="text"
                placeholder="Type what you need..."
                value={otherLookingToDo}
                onChange={(e) => setOtherLookingToDo(e.target.value)}
                className="mt-4 focus-visible:ring-[var(--primary)] focus-visible:border-[var(--primary)]"
                autoFocus
              />
            )}
          </div>
        );

      case 3:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start py-4">
            {/* Left Side */}
            <div className="lg:col-span-7 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm bg-white">
                  <img
                    src="/buzzaLogo.png"
                    alt="Buzza"
                    className="w-12 h-12 sm:w-14 sm:h-14"
                  />
                </div>
                <div className="flex flex-col">
                  <DialogTitle className="text-2xl font-bold text-gray-900 leading-tight">
                    Meet Buzza
                  </DialogTitle>
                  <p className="text-sm text-gray-500 font-medium">
                    Your Clover data on ChatGPT
                  </p>
                </div>
              </div>

              <div className="text-sm text-gray-600 leading-relaxed">
                Ask questions about your sales, customers, and products — get
                instant answers right in ChatGPT.
              </div>

              <div className="space-y-2 p-3 sm:p-2 rounded-2xl bg-gradient-to-br from-pink-50 via-teal-50 to-green-50 border border-teal-100 shadow-sm">
                <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  Want to join as a{" "}
                  <span className="text-teal-600">Founding Merchant</span>?
                </p>
                <p className="text-xs text-gray-600 leading-normal">
                  Only 10 merchants will get Buzza{" "}
                  <span className="text-teal-600">free forever</span>. Be the
                  first to access and give feedback.
                </p>
              </div>

              {/* ✅ Show ChatPreview on mobile too (placed between copy & phone input) */}
              <div className="lg:hidden">
                <ChatPreview />
              </div>

              <div className="space-y-3 pt-2">
                <p className="text-sm font-semibold text-gray-700">
                  📱 Where should we send your invite?
                </p>

                {/* ✅ stack on mobile, row on sm+ */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select
                    value={selectedCountry}
                    onValueChange={setSelectedCountry}
                  >
                    <SelectTrigger className="w-full sm:w-28 h-10 border-gray-200">
                      <SelectValue placeholder="US" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.flag} {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    type="tel"
                    placeholder="(555) 000-0000"
                    value={formatPhoneDisplay(phoneNumber)}
                    onChange={handlePhoneChange}
                    className={cn(
                      "w-full sm:flex-1 h-10 border-gray-200",
                      phoneError && "border-red-500 focus-visible:ring-red-500"
                    )}
                    inputMode="numeric"
                    autoComplete="tel"
                  />
                </div>

                {phoneError && (
                  <p className="text-xs text-red-500">{phoneError}</p>
                )}

                <p className="text-[10px] text-gray-400 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> We'll notify you when Buzza is
                  ready. No spam.
                </p>
              </div>
            </div>

            {/* Right Side: only desktop */}
            <div className="hidden lg:block lg:col-span-5 lg:sticky lg:top-0">
              <ChatPreview />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <style>{`
        [data-radix-dialog-overlay] {
          backdrop-filter: blur(4px);
        }
      `}</style>

      <DialogContent
        className="w-[95vw] sm:max-w-[92vw] lg:max-w-[55vw] xl:max-w-[50vw] 2xl:max-w-[65vw] p-0 gap-0 border-0 bg-white overflow-hidden rounded-2xl flex flex-col max-h-[90vh]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none z-10"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-4 pb-2 flex-shrink-0">
          <p className="text-sm text-gray-500 mb-1.5">
            Step {currentStep} of {totalSteps}
          </p>
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        </div>

        <div className="px-4 pb-2 overflow-y-auto flex-1 min-h-0">
          {renderStepContent()}
        </div>

        {/* ✅ Footer: stacked on mobile, original layout on sm+ */}
        <div className="border-t border-gray-100 flex-shrink-0">
          <div className="p-3 sm:hidden grid gap-2">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={cn(
                "w-full",
                currentStep === 1 && "opacity-50 cursor-not-allowed"
              )}
            >
              Previous
            </Button>

            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={isSubmitting}
              className="w-full text-gray-600 hover:text-gray-800"
            >
              Skip
            </Button>

            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              className={cn(
                "w-full rounded-full text-white font-medium transition-all",
                currentStep === totalSteps
                  ? "bg-teal-600 hover:bg-teal-700"
                  : "bg-[var(--primary)] hover:bg-[var(--primary)]/90"
              )}
            >
              {isSubmitting
                ? "Saving..."
                : currentStep === totalSteps
                  ? "Yes, I want founder access"
                  : "Next"}
            </Button>
          </div>

          <div className="hidden sm:grid grid-cols-3 items-center px-4 py-2">
            <div className="flex justify-start">
              <Button
                variant="ghost"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className={cn(
                  "text-gray-600 hover:text-gray-800",
                  currentStep === 1 && "opacity-50 cursor-not-allowed"
                )}
              >
                Previous
              </Button>
            </div>

            <div className="flex justify-start -ml-8">
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={isSubmitting}
                className="text-gray-600 hover:text-gray-800"
              >
                Skip
              </Button>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleNext}
                disabled={isSubmitting}
                className={cn(
                  "px-6 py-2.5 rounded-full text-white font-medium transition-all",
                  currentStep === totalSteps
                    ? "bg-teal-600 hover:bg-teal-700"
                    : "bg-[var(--primary)] hover:bg-[var(--primary)]/90"
                )}
              >
                {isSubmitting ? (
                  "Saving..."
                ) : currentStep === totalSteps ? (
                  <span className="flex items-center gap-1.5">
                    Yes, I want founder access
                  </span>
                ) : (
                  "Next"
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingSurveyPopup;
